// The dismissal, the panel, and the two cells the app was missing.
//
// The board's Status, Due and Owner columns were a native `<select>`, a native
// `<input type="date">` and another native `<select>`. They were the only place in
// crum where the operating system's widgets showed through the paper, and they were
// the widest-varying control in the product:
//
//   · Status was a select on most rows and a bordered chip on a blocked one, at
//     different widths, heights and radii, so the column did not line up with itself.
//   · Due printed the browser's locale format (`10/02/2026`) beside a table that
//     spells every other date `2026-10-02`, and an undated row printed the literal
//     string `mm/dd/yyyy` — repeated down the whole page.
//   · The owner select was painted with `var(--ink-2)`, which is not a token, so it
//     had no colour at all.
//
// Status needed nothing new: `PillSelect` in common.tsx was already the app's own
// dropdown-shaped-as-the-pill-it-sets, and `BlockedChip` is now that same pill. What
// was missing is here — a text menu and a date cell — and the dismissal all three
// share, which is the part that is easy to get subtly different in three places.
//
// **No modal, deliberately.** Nothing here opens a native dialog: crum inherited the
// habit from the gt app iframe, which has no `allow-modals`, and it is the better
// behaviour anyway.

import { useEffect, useRef, useState, type ReactNode } from 'react'

import { Icon } from './Icon'

/**
 * Close on a click outside, on Escape, and on a scroll that is not the menu's own.
 *
 * That last clause is load-bearing and was learned the hard way: a naive scroll
 * listener closes the menu the moment you scroll its own list, so the guard is by
 * TARGET rather than by event. Shared from here so three menus cannot disagree
 * about what dismisses them.
 */
export function useDismiss<T extends HTMLElement = HTMLSpanElement>(
  open: boolean,
  close: () => void,
) {
  const wrap = useRef<T>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    const onScroll = (e: Event) => {
      if (wrap.current?.contains(e.target as Node)) return
      close()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    document.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [open, close])
  return wrap
}

/**
 * The panel a menu drops. `float` radius, because it is above the page.
 *
 * Left-aligned and narrow on purpose: the board sheet scrolls sideways below 34rem,
 * and anything hanging off the right of a cell is clipped by that scrollport.
 */
export function Panel({ children, width = 176 }: { children: ReactNode; width?: number }) {
  return (
    <div
      className="rounded-float absolute top-full left-0 z-40 mt-1 overflow-auto border py-1 shadow-lg"
      style={{
        width,
        maxHeight: 260,
        background: 'var(--panel)',
        borderColor: 'var(--border)',
        overscrollBehavior: 'contain',
      }}
    >
      {children}
    </div>
  )
}

export interface MenuOption {
  key: string
  label: string
  /** drawn before the label: an avatar, a swatch, an icon */
  lead?: ReactNode
}

/**
 * A menu whose trigger is a line of text rather than a pill: the Owner column, and
 * anywhere else a cell names a record. `·` when empty, like every other empty cell.
 */
export function TextMenu({
  value,
  options,
  onPick,
  label,
  empty = '·',
  defaultOpen = false,
  chevron = false,
}: {
  value: string
  options: MenuOption[]
  onPick: (key: string) => void
  label: string
  empty?: string
  /** open on mount — for a menu that appears BECAUSE you asked to choose, like the
   *  board's "waits on", where a second click to open it would be a second click. */
  defaultOpen?: boolean
  /** show the chevron. On a bar control — "Order by", a filter — it is the only
   *  thing saying the words are a control at all. In a TABLE CELL it is left off:
   *  a caret on every row of a column is noise, and the column head plus the hover
   *  fill already say the cell is live. */
  chevron?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const wrap = useDismiss(open, () => setOpen(false))
  const current = options.find((o) => o.key === value && o.key !== '')

  return (
    <span ref={wrap} className="relative inline-flex min-w-0">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="rounded-inline text-body flex h-7 min-w-0 items-center gap-1.5 px-1 hover:bg-[var(--hover)]"
        style={{ color: current ? 'var(--fg)' : 'var(--faint)' }}
      >
        {current?.lead}
        <span className="truncate">{current?.label ?? empty}</span>
        {chevron && <Icon name="ChevronDown" size={12} className="shrink-0 opacity-60" />}
      </button>
      {open && (
        <Panel width={200}>
          <ul role="listbox">
            {options.map((o) => (
              <li key={o.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.key === value}
                  onClick={() => {
                    onPick(o.key)
                    setOpen(false)
                  }}
                  className="text-body flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[var(--hover)]"
                >
                  {o.lead}
                  <span className="truncate">{o.label}</span>
                  {o.key === value && (
                    <Icon
                      name="Check"
                      size={12}
                      className="ml-auto shrink-0"
                      style={{ color: 'var(--muted)' }}
                    />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </span>
  )
}

/**
 * A date, spelled the way the rest of the app spells one.
 *
 * The cell shows ISO in mono — `2026-10-02`, the house style, tabular so a column of
 * them lines up — and `·` when there is nothing, which is what every other empty cell
 * in crum shows. The native picker is still the picker, because its keyboard and
 * locale behaviour are worth keeping; it just lives in the popover now instead of in
 * the table, where its placeholder was printing `mm/dd/yyyy` down the page.
 */
export function DateCell({
  value,
  onChange,
  label,
  tone,
  placeholder = '·',
}: {
  value: string
  onChange: (iso: string) => void
  label: string
  /** a CSS colour for the date itself: overdue, due soon, set */
  tone?: string | undefined
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const wrap = useDismiss(open, () => setOpen(false))

  return (
    <span ref={wrap} className="relative inline-flex min-w-0">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="num rounded-inline text-meta flex h-7 min-w-0 items-center px-1 hover:bg-[var(--hover)]"
        style={{ color: value ? (tone ?? 'var(--fg)') : 'var(--faint)' }}
      >
        <span className="truncate">{value || placeholder}</span>
      </button>
      {open && (
        <Panel width={194}>
          <div className="flex flex-col items-start gap-1.5 px-2 py-1">
            <input
              autoFocus
              type="date"
              value={value}
              aria-label={label}
              onChange={(e) => onChange(e.target.value)}
              className="rounded-control text-body w-full border px-2 py-1 outline-none"
              style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
            />
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange('')
                  setOpen(false)
                }}
                className="rounded-inline text-meta px-1 hover:bg-[var(--hover)]"
                style={{ color: 'var(--muted)' }}
              >
                Clear
              </button>
            )}
          </div>
        </Panel>
      )}
    </span>
  )
}
